<!-- =========================================================================================
	File Name: App.vue
	Description: Main vue file - APP
	----------------------------------------------------------------------------------------
	Project: MISA mTax WebApp
	Author: MISA JSC
	Author URL: http://www.misa.com.vn
========================================================================================== -->

<template>
	<div id="app">
		<!-- Slot MISA Notification In-App v2 (chỉ hiển thị khi enabled: true) -->
		<div id="misa-notif-top-slot" v-if="misaNotifyV2Enabled"></div>
		<!-- Container cũ v1 (ẩn khi enabled: true) -->
		<router-view />
		<div id="message-box"></div>
	</div>
</template>

<script>
import themeConfig from '@/../themeConfig.js';
import messageBox from '@/common/messageBox';
import { initMisaNotification } from '@/integrations/misaNotification';
import EventBusGlobal, { GlobalEventName } from '@/sys/eventBusGlobal';
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import {
	ModuleLicenseInfo,
	ModuleConfig,
	ModuleNotification,
} from '@/store/module-const';

export default {
	watch: {
		'$store.state.theme'(val) {
			this.toggleClassInBody(val);
		},
		'$store.state.auth.expiredDate': {
			immediate: true,
			async handler() {
				const me = this;
				// Nếu đã khởi tạo
				if (me._notificationInitialized) return;

				// Kiểm tra xem đã đăng nhập hay chưa
				const isAuthenticated = await me.$auth.isAuthenticated();
				if (!isAuthenticated) return;

				// Đánh dấu cờ
				me._notificationInitialized = true;

				// Có cho phép sử dụng V2 không
				if (me.misaNotifyV2Enabled) {
					await me.connectMisaNotificationV2();
				}
			},
		},
	},
	computed: {
		...mapGetters(ModuleLicenseInfo, ['SumanLicenseInfo']),
		misaNotifyV2Enabled() {
			const config = window.__env && window.__env.misaNotifyConfigV2;
			return config && config.enabled === true;
		},
	},
	created() {
		const me = this;
		me.popupItems = {};
		me.getConfigInfo();
		window.addEventListener('storage', function (e) {
			//Nếu thay đổi người dùng đăng nhập
			if (e.key === 'userInfo') {
				if (e.oldValue) {
					window.oldUser = JSON.parse(e.oldValue);
				} else if (window.oldUser) {
					let oldUser = window.oldUser;
					let newUser = JSON.parse(e.newValue);
					if (newUser.UserId !== oldUser.UserId) {
						messageBox
							.showError(
								[
									'Tài khoản ',
									oldUser.FullName,
									' đã đăng xuất khỏi trình duyệt. Vui lòng nạp lại trang.',
								].join('')
							)
							.then((res) => {
								location.reload();
							});
					}
				}
			}
		});
		window.onfocus = function () {
			let context = sessionStorage.getItem('context');
			if (context) {
				localStorage.setItem('lastContext', context);
			}
		};
	},
	mounted() {
		const me = this;
		me.toggleClassInBody(themeConfig.theme);
		if (me.misaNotifyV2Enabled) {
			me.initMisaNotificationV2();
		} else {
			me.setIFrameMisaNotify();
		}
	},
	beforeDestroy() {
		this.teardownNotifResizeObserver();
	},
	methods: {
		...mapActions(ModuleConfig, ['getConfigInfo']),
		...mapActions(ModuleNotification, ['getMisaNotificationV2Token']),
		...mapMutations(ModuleNotification, ['updateMisaNotifShowing']),
		toggleClassInBody(className) {
			if (className == 'dark') {
				if (document.body.className.match('theme-semi-dark')) {
					document.body.classList.remove('theme-semi-dark');
				}
				document.body.classList.add('theme-dark');
			} else if (className == 'semi-dark') {
				if (document.body.className.match('theme-dark')) {
					document.body.classList.remove('theme-dark');
				}
				document.body.classList.add('theme-semi-dark');
			} else {
				if (document.body.className.match('theme-dark')) {
					document.body.classList.remove('theme-dark');
				}
				if (document.body.className.match('theme-semi-dark')) {
					document.body.classList.remove('theme-semi-dark');
				}
			}
		},
		/**
		 * nhúng iframe của Misa Notify
		 * @author dqduy 04.03.2024
		 */
		setIFrameMisaNotify() {
			let metaTag = document.createElement('meta');
			//Gán các thuộc tính của thẻ meta
			metaTag.setAttribute('name', 'misa-notify');
			metaTag.setAttribute(
				'integrationurl',
				window.__env.misaNotifyConfig.integrationURL
			);
			metaTag.setAttribute('appcode', window.__env.misaNotifyConfig.appCode);
			metaTag.setAttribute('headercontainer', '#misa_notify_header_container');
			// Thêm thẻ meta vào thẻ head
			document.head.appendChild(metaTag);

			//Tạo một thẻ script mới
			let script = document.createElement('script');
			script.src = window.__env.misaNotifyConfig.cdn;
			script.async = true; // Gán thuộc tính async cho thẻ script
			//Thêm thẻ script vào thẻ head
			document.head.appendChild(script);
		},
		async initMisaNotificationV2() {
			const me = this;
			const cfg = window.__env && window.__env.misaNotifyConfigV2;

			// Load CDN script nếu chưa load (chỉ khi đã login)
			if (cfg && cfg.cdn && !window.MISA_Notification) {
				var s = document.createElement('script');
				s.src = cfg.cdn;
				s.setAttribute('data-channel', cfg.dataChannel || 'stable');
				s.setAttribute('data-modules', cfg.dataModules || 'incident,inApp');
				s.setAttribute('data-appkey', cfg.dataAppKey);
				s.async = true;
				document.head.appendChild(s);
			}

			// Thiết lập event bus cho incident notifications
			if (
				window.__pendingIncidentEvent &&
				EventBusGlobal._incidentListenerReady
			) {
				EventBusGlobal.$emit(
					GlobalEventName.ShowMisaIncidentNotification,
					window.__pendingIncidentEvent
				);
				window.__pendingIncidentEvent = null;
			}

			document.addEventListener('misa-notify-incident-show', function (event) {
				if (!event.detail || event.detail.inAppPosition != 1) return;
				event.detail.isShow = true;
				if (EventBusGlobal._incidentListenerReady) {
					EventBusGlobal.$emit(
						GlobalEventName.ShowMisaIncidentNotification,
						event.detail
					);
				} else {
					window.__pendingIncidentEvent = event.detail;
				}
			});

			document.addEventListener('misa-notify-incident-hide', function (event) {
				EventBusGlobal.$emit(GlobalEventName.ShowMisaIncidentNotification, {
					isShow: false,
				});
			});

			me.$nextTick(() => {
				me.setupNotifResizeObserver();
			});
		},

		async connectMisaNotificationV2() {
			const me = this;
			// Thực hiện lấy Token của NotificationV2
			let tokenNoitify = await this.getMisaNotificationV2Token();
			if (!tokenNoitify) return;

			// Thực hiện khởi tạo
			initMisaNotification({
				getToken: tokenNoitify,
				onAuthError: function (ex) {
					console.log('onAuthError misa notification v2:' + ex);
					// router.push('/login');
				},
				inApp: function () {
					window.MISA_Notification.inApp.init({
						language: 'vi',
						headerTarget: '#misa-notif-top-slot',
						afterHeaderShow: true,
						afterShow: true,
						onSnooze: function (data) {
							// Đã nhấn "Để sau" → Misa Noti ẩn, banner license được phép hiển thị
							me.updateMisaNotifShowing(false);
							EventBusGlobal.$emit(
								GlobalEventName.misaNotifVisible,
								false
							);
						},
						onOpen: function () {
							// Misa Noti đang hiển thị → ưu tiên Misa Noti, ẩn banner license
							me.updateMisaNotifShowing(true);
							EventBusGlobal.$emit(
								GlobalEventName.misaNotifVisible,
								true
							);
						},
						onMessage: function (data) {
							me.updateMisaNotifShowing(true);
							EventBusGlobal.$emit(
								GlobalEventName.misaNotifVisible,
								true
							);
						},
					});
					window.MISA_Notification.inApp.connect();

					// tính toán lại vị trí hiển thị của giao diện
					me.$nextTick(() => {
						me.setupNotifResizeObserver();
					});
				},
			});
		},
		/**
		 * ResizeObserver theo dõi chiều cao #misa-notif-top-slot
		 * set --notif-height trên :root để sidebar/navbar/router-view tính toán lại top & height
		 * Logic dùng chung đặt ở $ms.commonFunc.observeElementHeight
		 */
		setupNotifResizeObserver() {
			const me = this;
			const slot = document.getElementById('misa-notif-top-slot');
			me.$ms.commonFunc.observeElementHeight(slot, '--notif-height');
		},
		teardownNotifResizeObserver() {
			this.$ms.commonFunc.disposeElementHeightObserver('--notif-height');
		},
	},
};
</script>

<style lang='scss'>
@media screen and (max-width: 1366px) {
	@import '@/assets/scss/_sm-variables.scss';
	@import '@/assets/scss/common/app.scss';
}
@media screen and (min-width: 1367px) {
	@import '@/assets/scss/_variables.scss';
	@import '@/assets/scss/common/app.scss';
}
</style>